import { captureMessage, setTag } from '@sentry/nextjs';
import IconCheck from 'assets/icons/check-toast-refferal.svg';
import IconCircleCheck from 'assets/icons/circle-check.svg';
import IconClose from 'assets/icons/close.svg';
import cn from 'classnames';
import { Gtm } from 'tracking';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { ToastOptions, toast } from 'react-toastify';
import { resolveTranslation } from './translations';
import { ElementType, FC, ReactNode } from 'react';

// Server-side rendering check
if (typeof window === 'undefined') {
  // No-op for SSR
}

const AUTOCLOSE_DURATION = 5000;

/**
 * Input for FormattedHTMLMessage
 * Since it's difficult to retrieve props interface from class component, FormattedHTMLMessage,
 * these are just approximate input built from scratch.
 * Not ideal but I assume it's okay since we can detect errors on build time when the definitions has become different.
 */
interface FormattedHTMLMessageProps extends Omit<MessageDescriptor, 'id'> {
  values?: Record<string, any>;
  tagName?: ElementType<any>;
}

interface ErrorMessageProps extends FormattedHTMLMessageProps {
  /**
   * Actually message Id, in our case such as 'account:main-page:title'.
   * If you want to just show arbitrary string, use defaultMessage instead
   * */
  error: MessageDescriptor['id'];
  fallbackError?: string;
  /** Amazon request ID from api header to track down one specific api call */
  xAmznRequestId?: string;
  /** Amazon trace ID from api header to track down one specific api call */
  xAmznTraceId?: string;
  /** Api route to track down one specific api call in Sentry */
  apiRoute?: string;
  /** Data to track down one specific api call in Sentry */
  data?: string;
  /** Headers to track down one specific api call in Sentry */
  headers?: string;
}

type _ErrorMessageProps = ErrorMessageProps;

type ShowErrorToastProps = _ErrorMessageProps & ToastOptions;

const ErrorTranslation: FC<ErrorMessageProps> = ({
  error,
  fallbackError,
  defaultMessage,
  values,
}) => {
  const intl = useIntl();
  try {
    const possibleTranslations = [error, fallbackError, !defaultMessage && 'error:unknown'].filter(
      (key) => typeof key === 'string'
    ) as string[];
    return (
      resolveTranslation(intl, possibleTranslations, values) || defaultMessage || (null as any)
    );
  } catch (error) {
    return null;
  }
};

export const showErrorToast = ({
  error,
  fallbackError,
  defaultMessage,
  values,
  autoClose = 5000,
  caller,
  xAmznTraceId,
  xAmznRequestId,
  apiRoute,
  data,
  headers,
  ...toastProps
}: ShowErrorToastProps & { caller?: string }): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const timestamp = new Date().getTime().toString();
  const uniqueId = Math.random().toString(36).substring(2);

  Gtm.track.viewElement({
    element: ['Error Toast', uniqueId, timestamp],
  });

  setTag('errorToastId', uniqueId);

  if (typeof error === 'string' && error.includes('not found')) {
    setTag('errorType', 'product-not-found');
  }

  captureMessage(`Error Toast | ${apiRoute ? apiRoute : 'other'}`, {
    level: 'error',
    extra: {
      uniqueId,
      timestamp,
      error,
      caller,
      xAmznTraceId,
      xAmznRequestId,
      data,
      headers,
    },
    tags: {
      apiRoute,
    },
  });

  const toastId = (error || fallbackError || defaultMessage || 'error:unknown') as string;

  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      autoClose,
    });
  } else {
    toast.error(
      <ErrorTranslation
        error={error}
        fallbackError={fallbackError}
        defaultMessage={defaultMessage}
        values={values}
      />,
      { toastId, autoClose, ...toastProps }
    );
  }
};

interface SuccessMessageProps extends FormattedHTMLMessageProps {
  /**
   * Actually message Id, in our case such as 'account:main-page:title'.
   * If you want to just show arbitrary string, use defaultMessage instead
   * */
  message: MessageDescriptor['id'];
  couponToast?: boolean;
  iconCheck?: boolean;
  iconClassName?: string;
  className?: string;
  variation?: 'old' | 'new';
}

type _SuccessMessageProps = SuccessMessageProps;

export type ShowSuccessToastProps = _SuccessMessageProps & ToastOptions;

const CloseButton = (): ReactNode => <IconClose className="my-auto! h-3! w-3! text-[#3ECBAF]!" />;

const toastVariants = {
  new: 'bg-success-main! text-background-paper! border-none! rounded-md!',
  old: 'bg-primary-main!',
};

export const showSuccessToast = ({
  variation = 'old',
  message,
  defaultMessage,
  description,
  values,
  tagName,
  autoClose = AUTOCLOSE_DURATION,
  couponToast,
  iconCheck = false,
  iconClassName,
  progressClassName,
  className,
  ...toastProps
}: ShowSuccessToastProps): void => {
  const toastId = (message || defaultMessage) as string;
  if (typeof window === 'undefined' || !toastId) {
    return;
  }

  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      autoClose,
    });
  } else {
    toast.success(
      <>
        {iconCheck && (
          <IconCircleCheck className={cn('mr-2 inline-block h-6 w-6', iconClassName)} />
        )}
        <FormattedMessage
          id={message}
          defaultMessage={defaultMessage}
          {...{
            description,
            values,
            tagName,
          }}
        />
      </>,
      {
        toastId,
        progressClassName,
        autoClose: couponToast ? false : autoClose,
        ...(couponToast && { position: 'top-right' }),
        ...(couponToast && { closeButton: <CloseButton /> }),
        ...toastProps,
        className: couponToast
          ? 'bg-[#E2F4F4]! border! border-[#80D1CD]! rounded-lg! text-primary-main! p-3! text-sm! m-2! shadow-lg!'
          : cn(toastVariants[variation], className),
      }
    );
  }
};

interface ShowSuccessReactToast {
  toastId: string;
  content: ReactNode;
  options?: ToastOptions;
}

export const showSuccessReactToast = ({
  toastId,
  content,
  options,
}: ShowSuccessReactToast): void => {
  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      autoClose: AUTOCLOSE_DURATION,
    });
  } else {
    toast.success(content, options);
  }
};

interface ShowNewSuccessToastProps {
  /** Id for toast. could be any string */
  toastId: string;
  /** Main content. if it's string, it's passed to format message  */
  mainContent?: ReactNode;
  /** Sub content. if it's string, it's passed to format message  */
  subContent?: ReactNode;
}

// TODO: it is not used anywhere, should we delete it?
/**
 * Success toast with new design
 */
export const showNewSuccessToast = ({
  toastId,
  mainContent,
  subContent,
}: ShowNewSuccessToastProps): void => {
  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      autoClose: AUTOCLOSE_DURATION,
    });
  } else {
    toast.info(
      <div className="flex flex-row flex-nowrap">
        <div className="">
          <IconCheck className="mr-2" />
        </div>
        <div>
          {mainContent && (
            <span className="text-primary-main text-base font-black">
              {typeof mainContent === 'string' ? (
                <p>
                  <FormattedMessage id={mainContent} />
                </p>
              ) : (
                <>{mainContent}</>
              )}
            </span>
          )}
          {subContent && (
            <span className="text-grey">
              {typeof subContent === 'string' ? (
                <p>
                  <FormattedMessage id={subContent} />
                </p>
              ) : (
                <>{subContent}</>
              )}
            </span>
          )}
        </div>
      </div>,
      {
        closeButton: <IconClose className="text-grey mt-1 h-4 w-4" />,
        position: 'top-right',
        autoClose: AUTOCLOSE_DURATION,
        className:
          'bg-other-white! border! border-[#80D1CD]! rounded-lg! text-primary-main! text-sm! shadow-lg! p-4!',
      }
    );
  }
};

interface ShowRefferalSuccessToast {
  message: ReactNode;
  id: string;
}
/**
 * @TODO : migrate to generic function
 */
export const showReferralSuccessToast = ({ message, id }: ShowRefferalSuccessToast): void => {
  if (toast.isActive(id)) {
    toast.update(id, {
      autoClose: AUTOCLOSE_DURATION,
    });
  } else {
    toast.success(
      <div className="flex flex-row flex-nowrap">
        <div>
          <IconCheck className="mx-2 flex" />
        </div>
        <div>{message}</div>
        <div className="h-0 w-5">
          <CloseButton />
        </div>
      </div>,
      {
        className:
          'bg-other-white! border! border-[#80D1CD]! rounded-lg! text-primary-main! text-sm! shadow-lg!',
        position: 'top-right',
      }
    );
  }
};
